import type { PosCajeroConPin, PosEntradaConPin } from "@/platform/types";

/**
 * La caja sin sesión (#346 AC-2; §2.6 de OFFLINE-POS-RONDA-2.md, D23 y D24).
 * Sin una sesión guardada, el cajero desbloquea la caja con su PIN si la
 * terminal tiene un mandato vigente. No hay token: el usuario es local, se arma
 * con lo que guarda el equipo, y sólo abre el mostrador del local de la caja.
 * Las ventas suben cuando alguien inicia sesión (D8).
 *
 * No se persiste: al reabrir la app, la caja vuelve a pedir el PIN.
 */

export interface CajaDeLaSesion {
  businessId: string;
  warehouseId: string;
  installationId: string;
}

/**
 * Lo que la app lee del usuario en el mostrador. Es del cajero que abrió la
 * caja; si después entra otro con su PIN, cambia el cajero
 * (`useCajeroDeLaCaja`), no este usuario: cambiarlo volvería a montar el
 * mostrador.
 */
export interface UsuarioDeLaCaja {
  id: string;
  name: string;
  lastName: string;
  businessId: string;
  businessName: string | null;
  /** No es el dueño: `WarehouseProtect` lo deja sólo en su local. */
  type: "employee";
  assignedWarehouses: string[];
  /** Sin módulos: `can()` no le abre nada. La ruta la decide la sesión. */
  modules: string[];
  sesionDeCaja: CajaDeLaSesion;
}

export function usuarioDeLaCaja(
  entrada: Pick<
    PosEntradaConPin,
    "businessId" | "warehouseId" | "installationId" | "negocio"
  >,
  cajero: PosCajeroConPin
): UsuarioDeLaCaja {
  return {
    id: cajero.userId,
    name: cajero.nombre,
    lastName: "",
    businessId: entrada.businessId,
    businessName: entrada.negocio,
    type: "employee",
    assignedWarehouses: [entrada.warehouseId],
    modules: [],
    sesionDeCaja: {
      businessId: entrada.businessId,
      warehouseId: entrada.warehouseId,
      installationId: entrada.installationId,
    },
  };
}

export function esSesionDeCaja(usuario: unknown): usuario is UsuarioDeLaCaja {
  return !!(usuario as { sesionDeCaja?: unknown } | null | undefined)
    ?.sesionDeCaja;
}

/** El mostrador del local de la caja: lo único que abre la sesión de caja. */
export const rutaDeLaCaja = (caja: Pick<CajaDeLaSesion, "warehouseId">) =>
  `/ventas/crear-rapida/${caja.warehouseId}`;

export function esRutaDeLaCaja(
  caja: Pick<CajaDeLaSesion, "warehouseId">,
  pathname: string
): boolean {
  return pathname.replace(/\/+$/, "") === rutaDeLaCaja(caja);
}

/** El mostrador de algún local: la misma ruta, con otro `warehouseId`. */
export function esRutaDelMostrador(pathname: string): boolean {
  return /^\/ventas\/crear-rapida\/[^/]+\/?$/.test(pathname);
}
